import { useState, useEffect } from 'react';
import SchemaOrg from '../../components/base/SchemaOrg';

export default function CasesPage() {
  const [activeFilter, setActiveFilter] = useState('全部');

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    const pageTitle = '施工案例｜水塔清洗水管疏通實績｜永業環保';
    const pageDesc = '永業環保5000+真實施工案例分享，包含水塔清洗、水管疏通、水溝清理、內視鏡檢查等服務案例。見證20年專業品質，服務桃園、新北、台北、新竹、苗栗地區。';
    const pageKeywords = '施工案例,水塔清洗案例,水管疏通案例,水溝清理案例,清潔服務實績';
    const pageUrl = `${siteUrl}/cases`;

    document.title = pageTitle;

    const setMeta = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', pageDesc);
    setMeta('meta[name="keywords"]', 'content', pageKeywords);
    setMeta('link[rel="canonical"]', 'href', pageUrl);
    setMeta('meta[property="og:title"]', 'content', pageTitle);
    setMeta('meta[property="og:description"]', 'content', pageDesc);
    setMeta('meta[property="og:url"]', 'content', pageUrl);
    setMeta('meta[name="twitter:title"]', 'content', pageTitle);
    setMeta('meta[name="twitter:description"]', 'content', pageDesc);
  }, [siteUrl]);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${siteUrl}/cases#collectionpage`,
    url: `${siteUrl}/cases`,
    name: '施工案例 - 永業環保',
    description: '永業環保真實施工案例分享，包含水塔清洗、水管疏通、水溝清理、內視鏡檢查等服務案例',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首頁',
          item: siteUrl
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: '施工案例',
          item: `${siteUrl}/cases`
        }
      ]
    }
  };

  const filters = ['全部', '水塔清洗', '水管疏通', '水溝清洗', '內視鏡檢查'];

  const cases = [
    {
      id: 1,
      category: '水塔清洗',
      title: '新北市林口區社區水塔清洗',
      location: '新北市林口區',
      date: '2022年3月',
      description: '大型社區水塔清洗，容量15噸，清洗前水質混濁，清洗後水質清澈透明，住戶反應良好。',
      image: 'https://public.readdy.ai/ai/img_res/fa02d8a5299c7f21585703842593bfd4.jpg',
      tags: ['社區', '15噸', '定期保養']
    },
    {
      id: 2,
      category: '水管疏通',
      title: '桃園市中壢區餐廳油脂管線疏通',
      location: '桃園市中壢區',
      date: '2023年8月',
      description: '餐廳廚房排水管嚴重堵塞，使用高壓水刀疏通技術，成功清除大量油脂堆積，恢復正常排水。',
      image: 'https://readdy.ai/api/search-image?query=plumber%20operating%20high%20pressure%20water%20jet%20machine%20cleaning%20grease%20clogged%20kitchen%20drain%20pipe%20on%20floor%20realistic%20photo%20commercial%20kitchen%20environment%20yellow%20safety%20equipment%20hose%20connected%20to%20pipe%20opening&width=600&height=400&seq=case-pipe-real-1&orientation=landscape',
      tags: ['餐廳', '油脂堵塞', '高壓水刀']
    },
    {
      id: 3,
      category: '水溝清洗',
      title: '台北市信義區辦公大樓排水溝清理',
      location: '台北市信義區',
      date: '2021年11月',
      description: '辦公大樓地下室排水溝長期未清理，積水嚴重，清理後排水順暢，解決淹水問題。',
      image: 'https://readdy.ai/api/search-image?query=worker%20in%20safety%20gear%20using%20shovel%20and%20vacuum%20truck%20hose%20to%20clean%20sludge%20from%20open%20concrete%20drainage%20channel%20underground%20parking%20lot%20realistic%20photo%20dark%20muddy%20sediment%20removed%20Taiwan&width=600&height=400&seq=case-drain-real-1&orientation=landscape',
      tags: ['辦公大樓', '地下室', '積水處理']
    },
    {
      id: 4,
      category: '內視鏡檢查',
      title: '新竹市東區透天厝管線內視鏡檢測',
      location: '新竹市東區',
      date: '2023年4月',
      description: '透天厝排水管經常堵塞，使用內視鏡檢查發現管線破損，協助客戶精準定位問題點。',
      image: 'https://readdy.ai/api/search-image?query=technician%20holding%20pipe%20inspection%20camera%20crawler%20robot%20inserting%20into%20drain%20pipe%20opening%20on%20ground%20realistic%20photo%20monitor%20showing%20cracked%20pipe%20interior%20on-site%20residential%20Taiwan%20close-up&width=600&height=400&seq=case-endo-real-1&orientation=landscape',
      tags: ['透天厝', '管線破損', '精準定位']
    },
    {
      id: 5,
      category: '水塔清洗',
      title: '桃園市桃園區工廠水塔清洗消毒',
      location: '桃園市桃園區',
      date: '2022年9月',
      description: '食品工廠水塔清洗消毒，符合衛生標準，提供清洗證明文件，通過衛生局檢查。',
      image: 'https://readdy.ai/api/search-image?query=worker%20in%20white%20protective%20suit%20and%20helmet%20descending%20into%20large%20industrial%20water%20tank%20with%20cleaning%20equipment%20disinfection%20spray%20realistic%20photo%20factory%20rooftop%20Taiwan%20bright%20daylight&width=600&height=400&seq=case-wt-real-2&orientation=landscape',
      tags: ['工廠', '消毒', '衛生檢查']
    },
    {
      id: 6,
      category: '水管疏通',
      title: '新北市板橋區住宅主管線疏通',
      location: '新北市板橋區',
      date: '2024年1月',
      description: '住宅主管線嚴重堵塞，多處排水口無法排水，使用專業設備疏通，恢復正常使用。',
      image: 'https://readdy.ai/api/search-image?query=plumber%20kneeling%20on%20floor%20feeding%20high%20pressure%20water%20jetting%20hose%20into%20residential%20drain%20pipe%20opening%20bathroom%20floor%20tiles%20realistic%20photo%20Taiwan%20apartment%20close-up%20water%20splashing&width=600&height=400&seq=case-pipe-real-2&orientation=landscape',
      tags: ['住宅', '主管線', '嚴重堵塞']
    },
    {
      id: 7,
      category: '水溝清洗',
      title: '苗栗縣竹南鎮社區排水溝清理',
      location: '苗栗縣竹南鎮',
      date: '2021年6月',
      description: '社區排水溝落葉堆積，雨季容易淹水，定期清理維護，確保排水順暢。',
      image: 'https://readdy.ai/api/search-image?query=workers%20using%20long%20tools%20to%20remove%20leaves%20and%20mud%20from%20open%20roadside%20concrete%20drainage%20ditch%20residential%20neighborhood%20realistic%20photo%20Taiwan%20outdoor%20daylight%20debris%20piled%20beside%20ditch&width=600&height=400&seq=case-drain-real-2&orientation=landscape',
      tags: ['社區', '落葉清理', '定期維護']
    },
    {
      id: 8,
      category: '內視鏡檢查',
      title: '台北市大安區老舊公寓管線檢測',
      location: '台北市大安區',
      date: '2023年12月',
      description: '老舊公寓管線年久失修，使用內視鏡檢查評估管線狀況，提供維修建議。',
      image: 'https://readdy.ai/api/search-image?query=technician%20watching%20monitor%20screen%20showing%20cracked%20corroded%20old%20pipe%20interior%20from%20endoscope%20camera%20inspection%20realistic%20photo%20on-site%20old%20apartment%20building%20Taiwan%20pipe%20condition%20assessment&width=600&height=400&seq=case-endo-real-2&orientation=landscape',
      tags: ['老舊公寓', '管線評估', '維修建議']
    },
    {
      id: 9,
      category: '水塔清洗',
      title: '新竹縣竹北市學校水塔清洗',
      location: '新竹縣竹北市',
      date: '2022年7月',
      description: '學校水塔定期清洗，確保師生用水安全，清洗過程符合教育局規定標準。',
      image: 'https://readdy.ai/api/search-image?query=two%20workers%20in%20uniform%20scrubbing%20interior%20walls%20of%20school%20rooftop%20water%20storage%20tank%20with%20long%20brushes%20dirty%20brown%20sediment%20visible%20realistic%20photo%20Taiwan%20school%20building%20rooftop%20sunny%20day&width=600&height=400&seq=case-wt-real-3&orientation=landscape',
      tags: ['學校', '定期清洗', '用水安全']
    }
  ];

  const filteredCases = activeFilter === '全部' 
    ? cases 
    : cases.filter(c => c.category === activeFilter);

  return (
    <>
      <SchemaOrg schema={schema} />
      {/* Hero 區域 */}
      <section className="relative pt-28 md:pt-32 pb-12 md:pb-20 bg-gradient-to-br from-[#f0f9ff] to-[#e0f2fe]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-12">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">施工案例</h1>
            <p className="text-base md:text-xl text-gray-600">超過5000個成功案例 見證我們的專業與品質</p>
          </div>

          {/* 篩選按鈕 */}
          <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
            <div className="flex gap-3 mx-auto flex-nowrap">
              {filters.map((filter) => (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    activeFilter === filter
                      ? 'bg-[#1e3a5f] text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 案例列表 */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filteredCases.map((caseItem) => (
              <div key={caseItem.id} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all">
                <div className="relative w-full h-56 md:h-64 overflow-hidden">
                  <img 
                    src={caseItem.image}
                    alt={caseItem.title}
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#1e3a5f] text-white px-4 py-1.5 rounded-full text-xs font-medium">
                      {caseItem.category}
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3">{caseItem.title}</h3>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                    <div className="flex items-center gap-1">
                      <i className="ri-map-pin-line"></i>
                      <span>{caseItem.location}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      <span>{caseItem.date}</span>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">{caseItem.description}</p>
                  
                  <div className="flex flex-wrap gap-2">
                    {caseItem.tags.map((tag, index) => (
                      <span key={index} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA 區域 */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-[#1e3a5f] to-[#2d5a8f]">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">需要專業的清潔服務嗎？</h2>
          <p className="text-base md:text-xl text-white/90 mb-6 md:mb-8">立即聯絡我們，讓永業環保為您提供最優質的服務</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="tel:0800-281-281" className="w-full sm:w-auto bg-white text-[#1e3a5f] px-8 py-4 rounded-full text-base md:text-lg font-medium hover:bg-gray-100 transition-all hover:scale-105 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line"></i>
              立即撥打 0800-281-281
            </a>
            <a href="https://line.me/ti/p/wJ4U8JR3pC" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto bg-transparent border-2 border-white text-white px-8 py-4 rounded-full text-base md:text-lg font-medium hover:bg-white hover:text-[#1e3a5f] transition-all hover:scale-105 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer">
              <i className="ri-line-fill"></i>
              線上預約
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
